import type { FeedLog } from '../../types/production'
import { getCurrentOperatorActorId } from '../../utils/operatorSession'
import { defaultDayTimeZone, farmDayOffset, farmWallClockToInstant } from '../../utils/farmTime'

// Crew Mode Field Bundle v0 — small pure helpers shared by the capture screens.

/**
 * §3 item 9: the feed type last used in this tank (same lot segment and
 * unit), from the device's feed-log copy; undefined when the tank has none.
 */
export const lastFeedTypeForTank = (
  logs: FeedLog[],
  tank: { lotId: string; productionUnitId: string }
): string | undefined => {
  let latest: FeedLog | undefined
  for (const log of logs) {
    if (log.lotId !== tank.lotId || log.productionUnitId !== tank.productionUnitId) continue
    if (!log.feedType?.trim()) continue
    if (!latest || String(log.recordedAt) > String(latest.recordedAt)) latest = log
  }
  return latest?.feedType?.trim() || undefined
}

/**
 * §3 item 6: the audit data for an operator's undo — automatic reason (no
 * extra tap) and the PIN-identified operator (D-4).
 */
export const buildOperatorUndoAudit = (reason: string): { reason: string; operatorActorId?: string } => {
  const operatorActorId = getCurrentOperatorActorId()
  return operatorActorId ? { reason, operatorActorId } : { reason }
}

/** §3 item 8: "Hoy / Ayer / Hace 2 días" → an ISO time on that day. */
export type CrewEntryDay = 0 | 1 | 2

export const recordedAtForEntryDay = (daysAgo: CrewEntryDay, now: Date = new Date(), timeZone?: string): string => {
  if (daysAgo === 0) return now.toISOString()
  // Late entries are placed at noon of that FARM day (findings #85): a stable,
  // clearly "that day" time in the farm's zone, whatever zone the phone is set to.
  const zone = timeZone ?? defaultDayTimeZone()
  const day = farmDayOffset(-daysAgo, zone, now)
  return farmWallClockToInstant(`${day}T12:00`, zone) ?? now.toISOString()
}
